// Politique de confidentialité — français. Traduction de courtoisie ; la version italienne prévaut.
// Les `id` et l'ordre des sections doivent correspondre à privacy.it.js (vérifié par privacy.test.js).
import { CONTROLLER, controllerAddressLine, hasPostalAddress } from "@/lib/legal";

// Il paese nella lingua di chi legge: CONTROLLER.country resta il valore postale.
const COUNTRY = "Italie";

const identity = [
  ["Responsable du traitement", CONTROLLER.legalName],
  CONTROLLER.tradeName && ["Nom commercial", CONTROLLER.tradeName],
  ["Forme juridique", "Entrepreneur individuel (impresa individuale) de droit italien"],
  ["Pays d'établissement", COUNTRY],
  CONTROLLER.vat && ["Numéro de TVA", CONTROLLER.vat],
  CONTROLLER.taxCode && ["Code fiscal italien", CONTROLLER.taxCode],
  CONTROLLER.reaNumber && ["Immatriculation REA", CONTROLLER.reaNumber],
  hasPostalAddress() && ["Siège", controllerAddressLine()],
  ["E-mail", CONTROLLER.email],
  CONTROLLER.pec && ["E-mail certifié (PEC)", CONTROLLER.pec],
  CONTROLLER.phone && ["Téléphone", CONTROLLER.phone],
].filter(Boolean);

const doc = {
  title: "Politique de confidentialité",
  subtitle:
    "Information relative au traitement des données à caractère personnel au sens des articles 13 et 14 du Règlement (UE) 2016/679 (RGPD) et du décret législatif italien 196/2003 modifié par le décret législatif 101/2018.",
  updatedLabel: "Dernière mise à jour",
  versionLabel: "Version",
  tocLabel: "Sommaire",
  authoritativeNote:
    "Ceci est une traduction de courtoisie. Le responsable du traitement est établi en Italie : en cas de divergence, la version italienne de la présente politique prévaut.",
  backLabel: "Retour au site",

  sections: [
    {
      id: "titolare",
      h: "1. Qui traite vos données",
      blocks: [
        {
          p: `Le responsable du traitement est ${CONTROLLER.legalName}, entrepreneur individuel ${
            hasPostalAddress() ? `établi à ${controllerAddressLine()}` : `établi en ${COUNTRY}`
          }, qui exploite le service VisioScreening (ci-après « le Service », accessible à l'adresse visioscreening.com).`,
        },
        {
          p: `Le responsable du traitement est joignable à l'adresse ${CONTROLLER.email} pour toute communication relative à la présente politique, y compris les demandes d'exercice des droits décrits à la section 10. Toute personne ayant besoin des données d'identification complètes du responsable — numéro de TVA et siège compris — peut les demander à cette même adresse et les recevra sans frais.`,
        },
        { table: { head: ["", ""], rows: identity } },
        {
          p: "Aucun délégué à la protection des données (DPO) n'a été désigné : le traitement ne relève pas des cas prévus à l'article 37 du RGPD, dès lors qu'il ne consiste ni en un suivi régulier et systématique à grande échelle, ni en un traitement à grande échelle de catégories particulières de données. Pour toute question relative aux données personnelles, vous pouvez écrire directement aux adresses indiquées ci-dessus.",
        },
      ],
    },

    {
      id: "ruoli",
      h: "2. Deux types de données, deux rôles distincts",
      blocks: [
        {
          p: "VisioScreening est un outil professionnel : celui qui l'utilise est un médecin, un opticien, un technicien de prévention ou un autre professionnel de santé qui fait passer des tests visuels à un travailleur sur écran. Cela produit deux catégories de données totalement distinctes, qu'il est essentiel de ne pas confondre.",
        },
        {
          table: {
            head: ["Données", "Qui est responsable du traitement", "Où elles se trouvent"],
            rows: [
              [
                "Données du professionnel qui utilise le Service (compte, cabinet, paiement)",
                `${CONTROLLER.legalName} est responsable du traitement`,
                "Sur les serveurs du Service",
              ],
              [
                "Données du travailleur testé (identité, réponses, résultats, compte rendu)",
                "Le professionnel ou l'établissement de santé est responsable de traitement autonome",
                "Exclusivement sur l'appareil du professionnel",
              ],
            ],
          },
        },
        {
          note: "Les données cliniques du travailleur ne sont jamais transmises à nos serveurs. Elles restent dans la mémoire du navigateur pendant la durée du dépistage, et le compte rendu PDF est généré localement, sur l'appareil du professionnel. Sur ces données, nous ne sommes ni responsable de traitement ni sous-traitant, car elles ne nous parviennent jamais. La section 4 explique en détail comment cela fonctionne.",
        },
      ],
    },

    {
      id: "dati",
      h: "3. Quelles données nous traitons et pourquoi",
      blocks: [
        {
          p: "Nous ne traitons que ce qui est nécessaire pour vous fournir un compte, vous permettre de générer des comptes rendus et encaisser le paiement. Nous ne réalisons aucun profilage, n'envoyons pas de newsletters non sollicitées, ne cédons aucune donnée à des tiers à des fins de marketing et n'utilisons aucun outil d'analyse comportementale.",
        },
        {
          table: {
            head: ["Catégorie de données", "Finalité", "Base légale", "Conservation"],
            rows: [
              [
                "Données du compte : adresse e-mail, nom (facultatif), mot de passe conservé uniquement sous forme d'empreinte bcrypt ; en cas de connexion avec Google, le nom, l'e-mail et la photo de profil transmis par Google",
                "Créer et gérer le compte, vous authentifier, permettre la récupération du mot de passe",
                "Art. 6(1)(b) RGPD — exécution du contrat",
                "Pendant toute la durée du compte et jusqu'à 30 jours après une demande de suppression (sauvegardes jusqu'à 90 jours)",
              ],
              [
                "Données du cabinet : dénomination et adresse de la structure, nom du médecin, logo et image de la signature que vous chargez",
                "Personnaliser l'en-tête du compte rendu PDF avec les données de votre cabinet",
                "Art. 6(1)(b) RGPD — exécution du contrat",
                "Jusqu'à la suppression du compte ou la modification de la configuration",
              ],
              [
                "Registre des comptes rendus : identifiant aléatoire du compte rendu, date et heure, langue, caractère gratuit ou payant, et empreinte SHA-256 salée de l'adresse IP",
                "Faire valoir la licence acquise, compter les comptes rendus gratuits déjà utilisés, détecter les abus du compte rendu gratuit (création en série de comptes)",
                "Art. 6(1)(b) et art. 6(1)(f) RGPD — exécution du contrat et intérêt légitime à prévenir les abus",
                "La ligne demeure pendant toute la durée du compte, car c'est la preuve de la licence acquise ; l'empreinte de l'IP est supprimée une fois écoulée la fenêtre de quelques jours pendant laquelle elle sert à détecter les abus",
              ],
              [
                "Données de paiement : identifiant de la session de paiement, montant, devise, statut, données de facturation et fiscales saisies sur Stripe",
                "Encaisser le paiement unique, émettre et conserver les documents comptables",
                "Art. 6(1)(b) RGPD — exécution du contrat ; art. 6(1)(c) RGPD — obligations fiscales et comptables",
                "Les documents comptables 10 ans à compter de l'écriture (art. 2220 du Code civil italien et législation fiscale) ; les données techniques de la transaction jusqu'à la suppression du compte",
              ],
              [
                "Données techniques et de sécurité : adresse IP, type de navigateur, horodatage des requêtes, tentatives de connexion échouées, jetons de session et de réinitialisation du mot de passe",
                "Maintenir le service en fonctionnement, le protéger contre les accès non autorisés, limiter les tentatives de forçage des mots de passe",
                "Art. 6(1)(f) RGPD — intérêt légitime à la sécurité des réseaux et de l'information",
                "Tentatives de connexion : quelques minutes. Jetons : jusqu'à expiration. Journaux des hébergeurs : généralement 30 jours",
              ],
              [
                "Correspondance : contenu des e-mails ou messages que vous nous adressez pour obtenir de l'aide",
                "Répondre à vos demandes et traiter d'éventuelles réclamations",
                "Art. 6(1)(b) et art. 6(1)(f) RGPD — exécution du contrat et intérêt légitime à la défense de nos droits",
                "Jusqu'à 24 mois après le dernier contact, sauf litige en cours",
              ],
            ],
          },
        },
        {
          p: "La fourniture des données signalées comme obligatoires dans les formulaires est nécessaire pour activer le compte et utiliser le Service : sans elles, il n'est techniquement pas possible de le fournir. La fourniture des autres données est libre et son absence n'affecte pas l'utilisation du Service.",
        },
      ],
    },

    {
      id: "pazienti",
      h: "4. Les données du travailleur testé",
      blocks: [
        {
          p: "Le questionnaire d'anamnèse, les données d'identité du travailleur, les réponses aux sept tests et le compte rendu final sont des données de santé, c'est-à-dire des catégories particulières de données au sens de l'article 9 du RGPD. Le Service est conçu pour ne jamais les recevoir.",
        },
        {
          ul: [
            "Les données saisies pendant le dépistage restent dans la mémoire du navigateur, sur l'appareil du professionnel, et ne sont transmises à nos serveurs à aucun moment.",
            "Le compte rendu PDF est composé intégralement dans le navigateur : il ne transite pas par nous, nous ne le stockons pas et n'en conservons aucune copie.",
            "Si vous interrompez le dépistage pour effectuer le paiement, les données saisies sont enregistrées temporairement dans le sessionStorage du navigateur pendant deux heures au maximum, afin de pouvoir reprendre là où vous vous étiez arrêté. Le sessionStorage est effacé à la fermeture du navigateur, et le logo ainsi que la signature n'y sont jamais écrits.",
            "La seule donnée qui atteint nos serveurs lorsque vous téléchargez un compte rendu est un identifiant aléatoire de celui-ci, dépourvu de toute référence au travailleur : il sert uniquement à décrémenter le compteur de comptes rendus disponibles.",
          ],
        },
        {
          note: "Dès lors que nous n'entrons jamais en possession des données de santé du travailleur, nous considérons que les conditions d'une désignation en qualité de sous-traitant au sens de l'article 28 du RGPD ne sont pas réunies. Cela étant, sur demande, nous signons malgré tout un contrat de sous-traitance ou délivrons une déclaration écrite décrivant l'architecture exposée ici : écrivez-nous à l'adresse indiquée à la section 14.",
        },
        {
          p: `Toutes les obligations relatives aux données du travailleur incombent au professionnel qui utilise le Service : déterminer la base légale du traitement (généralement l'art. 9(2)(h) du RGPD pour la médecine du travail, combiné à l'art. 41 du décret législatif italien 81/2008), remettre sa propre information à la personne concernée, conserver le compte rendu de manière sécurisée et respecter le secret professionnel. ${CONTROLLER.legalName} n'est pas responsable de l'usage que le professionnel fait du compte rendu une fois celui-ci généré.`,
        },
      ],
    },

    {
      id: "destinatari",
      h: "5. À qui nous communiquons les données",
      blocks: [
        {
          p: "Nous ne vendons ni ne cédons vos données. Nous les partageons uniquement avec les prestataires qui rendent le Service techniquement possible, désignés sous-traitants au sens de l'article 28 du RGPD et tenus de ne les traiter que selon nos instructions.",
        },
        {
          table: {
            head: ["Prestataire", "Rôle", "Données concernées"],
            rows: [
              ["Stripe Payments Europe, Ltd. (Irlande)", "Traitement des paiements par carte", "Données de facturation, montant, résultat. Les données de carte sont traitées directement par Stripe et ne nous sont jamais communiquées"],
              ["MongoDB, Inc. / MongoDB Atlas", "Base de données managée", "Ensemble des données de compte et de licence"],
              ["Render Services, Inc.", "Hébergement de l'application côté serveur", "Données transmises à l'API et journaux techniques"],
              ["Netlify, Inc.", "Hébergement et CDN du site et de l'application web", "Données de connexion (adresse IP, user agent)"],
              ["Prestataire d'e-mails transactionnels (Resend / fournisseur SMTP configuré)", "Envoi des e-mails système, comme la réinitialisation du mot de passe", "Adresse e-mail et contenu du message"],
              ["Google Ireland Limited", "Connexion avec Google, uniquement si vous choisissez de l'utiliser", "Identifiant, nom, e-mail et photo de profil Google"],
              ["Experts-comptables et conseils fiscaux", "Obligations administratives et fiscales", "Données de facturation"],
            ],
          },
        },
        {
          p: "Les données peuvent en outre être communiquées aux autorités publiques lorsque la loi l'impose ou lorsque cela est nécessaire pour constater, exercer ou défendre un droit en justice. La liste à jour des sous-traitants est disponible sur demande à " + CONTROLLER.email + ".",
        },
      ],
    },

    {
      id: "trasferimenti",
      h: "6. Transferts hors de l'Espace économique européen",
      blocks: [
        {
          p: "Certains des prestataires énumérés ci-dessus sont établis aux États-Unis ou peuvent traiter des données en dehors de l'Espace économique européen. Dans ce cas, le transfert repose sur l'une des garanties prévues au chapitre V du RGPD : une décision d'adéquation de la Commission européenne — en particulier l'EU-U.S. Data Privacy Framework pour les prestataires qui y adhèrent — ou les clauses contractuelles types adoptées par la Commission européenne par la décision d'exécution (UE) 2021/914, complétées si nécessaire par des mesures supplémentaires.",
        },
        {
          p: "Certains des services utilisés opèrent dans des régions situées hors de l'Union européenne, en particulier aux États-Unis. Vous pouvez à tout moment et sans frais demander l'indication à jour de la localisation des données ainsi qu'une copie des garanties mises en place pour chaque prestataire, en écrivant à " + CONTROLLER.email + ".",
        },
      ],
    },

    {
      id: "conservazione",
      h: "7. Combien de temps nous conservons les données",
      blocks: [
        { p: "Les durées de conservation sont indiquées par catégorie dans le tableau de la section 3. En résumé :" },
        {
          ul: [
            "Les données du compte sont conservées tant que le compte existe. L'application n'expose aucune fonction d'auto-suppression : l'effacement est effectué manuellement par le responsable du traitement, sur demande adressée à l'adresse indiquée à la section 14, sans retard excessif et en tout état de cause sous 30 jours, et porte sur le compte, la licence, la configuration du cabinet, les sessions et les registres de comptes rendus.",
            "L'empreinte de l'adresse IP sert à détecter les abus du compte rendu gratuit sur une fenêtre de quelques jours. Passé ce délai, elle n'a plus d'utilité et est supprimée lors des vérifications périodiques du responsable ; la ligne du compte rendu à laquelle elle était associée demeure, sans l'empreinte, car elle est la preuve de la licence.",
            "Les documents comptables et fiscaux liés au paiement sont conservés 10 ans, comme l'impose la loi italienne : cette obligation prime sur une demande de suppression et ne porte que sur les documents comptables, non sur tout l'historique technique du paiement.",
            "Les données techniques et de sécurité ont une durée de vie brève : les compteurs de tentatives de connexion durent quelques minutes, les jetons de session et de réinitialisation expirent d'eux-mêmes, et les journaux des hébergeurs suivent la rotation fixée par ces prestataires.",
            "Les sauvegardes gérées par le fournisseur de la base de données suivent la rotation du plan en vigueur : la suppression des systèmes actifs est immédiate, celle des sauvegardes intervient au cycle de rotation suivant.",
          ],
        },
        {
          note: "Le caractère manuel de l'effacement n'en réduit ni la portée ni les délais : l'article 17 du RGPD s'exerce en écrivant au responsable du traitement, et la demande est traitée dans les délais de la section 10. Si vous préférez vérifier que l'effacement a bien eu lieu, demandez-le dans le même e-mail et vous recevrez une confirmation écrite.",
        },
      ],
    },

    {
      id: "cookie",
      h: "8. Cookies et stockage local",
      blocks: [
        {
          p: "VisioScreening n'utilise ni cookies de profilage, ni cookies publicitaires, ni outils de statistiques ou d'analyse comportementale. Il n'y a donc aucune bannière de consentement : les technologies employées sont exclusivement techniques et nécessaires, et les lignes directrices de l'autorité italienne de protection des données du 10 juin 2021 n'exigent pas de consentement pour celles-ci.",
        },
        {
          table: {
            head: ["Nom", "Type", "Fonction", "Durée"],
            rows: [
              ["session_token", "Cookie technique (httpOnly, Secure)", "Maintient la session ouverte après la connexion", "Jusqu'à l'expiration de la session ou la déconnexion"],
              ["vs_token", "localStorage", "Copie du jeton de session, nécessaire car le site et l'API résident sur des domaines différents et certains navigateurs bloquent les cookies tiers", "Jusqu'à la déconnexion ou l'effacement des données du navigateur"],
              ["vs_lang", "localStorage", "Mémorise la langue choisie pour l'interface", "Jusqu'à l'effacement des données du navigateur"],
              ["visioscreen_defaults_v1", "localStorage", "Mémorise les valeurs par défaut du cabinet et du médecin, pour ne pas les ressaisir à chaque dépistage", "Jusqu'à l'effacement des données du navigateur"],
              ["vs_pending_report", "sessionStorage", "Conserve un dépistage interrompu pour le paiement, afin de pouvoir le reprendre", "2 heures maximum, et effacé à la fermeture du navigateur"],
            ],
          },
        },
        {
          p: "Le paiement s'effectue sur une page hébergée par Stripe, qui utilise ses propres cookies techniques et anti-fraude, selon sa propre politique. Les pages publiques ne chargent aucune ressource depuis un domaine tiers : images, polices et scripts sont servis depuis notre propre domaine, de sorte que la simple visite ne communique votre adresse IP à aucun prestataire externe. Vous pouvez effacer les cookies et le stockage local à tout moment depuis les réglages de votre navigateur ; désactiver les cookies techniques rend toutefois la connexion à l'application impossible.",
        },
      ],
    },

    {
      id: "sicurezza",
      h: "9. Sécurité",
      blocks: [
        { p: "Nous mettons en œuvre des mesures techniques et organisationnelles adaptées au risque, conformément à l'article 32 du RGPD, notamment :" },
        {
          ul: [
            "transmission des données exclusivement sur des connexions chiffrées (HTTPS/TLS) ;",
            "mots de passe conservés uniquement sous forme d'empreintes bcrypt, jamais en clair et jamais réversibles ;",
            "jetons de session dans des cookies httpOnly et Secure, avec expiration ;",
            "cloisonnement des données par compte : aucun utilisateur ne peut accéder à la configuration ou aux comptes rendus d'un autre ;",
            "limitation automatique des tentatives de connexion répétées ;",
            "adresses IP conservées uniquement sous forme d'empreintes SHA-256 avec un sel secret, non rattachables à l'adresse d'origine sans ce sel ;",
            "minimisation à la source : les données de santé ne quittent jamais l'appareil du professionnel.",
          ],
        },
        {
          p: "Aucune mesure de sécurité ne saurait être absolue. En cas de violation de données à caractère personnel présentant un risque pour les droits et libertés des personnes concernées, nous procéderons à la notification à l'autorité italienne de protection des données dans les 72 heures et, lorsque le risque est élevé, à l'information des personnes concernées, conformément aux articles 33 et 34 du RGPD.",
        },
      ],
    },

    {
      id: "diritti",
      h: "10. Vos droits",
      blocks: [
        { p: "Vous pouvez à tout moment exercer les droits reconnus par les articles 15 à 22 du RGPD :" },
        {
          ul: [
            "accès : obtenir la confirmation qu'un traitement est en cours et en recevoir une copie (art. 15) ;",
            "rectification : corriger des données inexactes ou compléter des données incomplètes (art. 16) ;",
            "effacement : obtenir la suppression des données que nous ne sommes pas tenus de conserver par la loi (art. 17) ;",
            "limitation : demander la suspension du traitement dans les cas prévus (art. 18) ;",
            "portabilité : recevoir les données que vous nous avez fournies dans un format structuré et lisible par machine, ou les faire transmettre à un autre responsable de traitement (art. 20) ;",
            "opposition : vous opposer à tout moment, pour des raisons tenant à votre situation particulière, aux traitements fondés sur l'intérêt légitime (art. 21).",
          ],
        },
        {
          p: `Pour exercer ces droits, écrivez à ${CONTROLLER.email}${CONTROLLER.pec ? ` ou, par e-mail certifié, à ${CONTROLLER.pec}` : ""}. Nous répondons dans un délai d'un mois à compter de la réception de la demande, prorogeable de deux mois en cas de demandes particulièrement complexes, ce dont nous vous informerons. L'exercice des droits est gratuit, sauf demandes manifestement infondées ou excessives.`,
        },
        {
          p: "Si vous estimez que le traitement de vos données viole le RGPD, vous avez le droit d'introduire une réclamation auprès de l'autorité italienne de protection des données — Garante per la protezione dei dati personali, Piazza Venezia 11, 00187 Rome, Italie ; téléphone +39 06 696771 ; e-mail garante@gpdp.it ; e-mail certifié protocollo@pec.gpdp.it ; www.garanteprivacy.it — ou auprès de l'autorité de contrôle de l'État membre dans lequel vous résidez. Votre droit à un recours juridictionnel n'en est pas affecté.",
        },
      ],
    },

    {
      id: "automatizzate",
      h: "11. Décisions automatisées et profilage",
      blocks: [
        {
          p: "Nous ne mettons en œuvre aucune décision automatisée ni aucun profilage au sens de l'article 22 du RGPD. Les résultats des tests sont calculés localement sur l'appareil, constituent une aide au dépistage et ne remplacent en aucun cas l'évaluation et le diagnostic du professionnel de santé.",
        },
      ],
    },

    {
      id: "minori",
      h: "12. Mineurs",
      blocks: [
        {
          p: "Le Service s'adresse à des professionnels dans l'exercice de leur activité et n'est pas destiné aux mineurs. Nous ne collectons pas sciemment de données concernant des mineurs lors de la création d'un compte. Si vous pensez qu'un mineur a créé un compte, signalez-le-nous et nous procéderons à sa suppression.",
        },
      ],
    },

    {
      id: "modifiche",
      h: "13. Modifications de la présente politique",
      blocks: [
        {
          p: "La présente politique peut être mise à jour pour tenir compte des évolutions du Service, des prestataires utilisés ou de la réglementation applicable. La version en vigueur est toujours publiée sur cette page, avec sa date de dernière mise à jour et son numéro de version. En cas de modifications substantielles, nous en informerons les utilisateurs enregistrés par e-mail ou au sein de l'application avant leur entrée en vigueur.",
        },
      ],
    },

    {
      id: "contatti",
      h: "14. Contact",
      blocks: [
        {
          p: `Pour toute question sur la présente politique ou sur le traitement de vos données à caractère personnel, écrivez à ${CONTROLLER.email}${CONTROLLER.pec ? ` ou à ${CONTROLLER.pec}` : ""}. C'est la même adresse que l'assistance, relevée quotidiennement et par choix délibéré : les demandes relatives aux données personnelles y sont identifiées et traitées comme telles. Si vous avez besoin d'une adresse postale du responsable — pour un envoi recommandé ou une réclamation formelle — demandez-la à cette adresse et elle vous sera fournie sans frais.`,
        },
      ],
    },
  ],
};

export default doc;
